/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// ส่งข้อความเองเข้ากลุ่ม Telegram (รอบ 2 ข้อ A6 · 25 ต.ค. 2569)
// เลือกกลุ่ม → พิมพ์หรือเลือกชุดข้อความสำเร็จรูป → ดูตัวอย่าง → ยืนยัน → OPTIONS /notify_setting/send_message
// ชุดข้อความสำเร็จรูป: บันทึก/แก้/ลบ ผ่าน save_message / delete_message (ตาราง notify_messages · 0030)
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { FetchData } from 'services/Request';

const ERR = {
  message_required: 'พิมพ์ข้อความก่อน (ไม่เกิน 2,000 ตัวอักษร)',
  message_name_required: 'ตั้งชื่อชุดข้อความก่อน (ไม่เกิน 100 ตัวอักษร)',
  channel_required: 'เลือกกลุ่มอย่างน้อย 1 กลุ่ม',
  channel_disabled: 'มีกลุ่มที่ปิดใช้งานหรือยังไม่มีเลขห้อง เลือกกลุ่มใหม่',
  telegram_token_missing: 'ร้านยังไม่ได้เชื่อมต่อบอต Telegram — แจ้งไอที',
  messages_table_missing: 'เซิร์ฟเวอร์ยังไม่รองรับ (ยังไม่รันสคริปต์ฐานข้อมูล 0030) — แจ้งไอที',
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์ตั้งค่าแจ้งเตือน',
};

export default function NotifySendDialog({ open, onClose, token, mod, onSent, onNotify }) {
  const [presets, setPresets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [picked, setPicked] = useState([]); // channel id ที่ติ๊ก
  const [presetId, setPresetId] = useState(0);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState('');
  const [step, setStep] = useState('edit'); // edit | confirm
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // {text, preview} ที่ผู้ใช้เห็นในขั้นยืนยัน = สิ่งที่ส่งจริง
  const [askDelete, setAskDelete] = useState(false);
  const [checking, setChecking] = useState(false);
  const seq = useRef(0); // พรีวิวที่ตอบช้ากว่าการพิมพ์ครั้งถัดไป ไม่ทับของใหม่
  const timer = useRef(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  const loadPresets = () =>
    FetchData('OPTIONS', `/${mod}/messages`, token, { _t: Date.now() })
      .then((r) => {
        if (!alive.current || r.status !== 'ok') return;
        setPresets(r.data || []);
        setChannels(r.channels || []);
      })
      .catch(() => {});

  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setPresetId(0);
    setText('');
    setPreview('');
    setStep('edit');
    setSaveName('');
    setConfirmed(null);
    setAskDelete(false);
    loadPresets();
  }, [open]);

  /* ตัวอย่างจากเซิร์ฟเวอร์ (escape + ตัวหนา แบบเดียวกับที่ส่งจริง) หน่วง 400 มิลลิวินาที */
  const askPreview = (value) => {
    clearTimeout(timer.current);
    if (!value.trim()) {
      setPreview('');
      return;
    }
    timer.current = setTimeout(() => {
      seq.current += 1;
      const mine = seq.current;
      FetchData('OPTIONS', `/${mod}/preview_template`, token, {
        code: 'manual',
        template: value,
        _t: Date.now(),
      })
        .then((r) => {
          if (alive.current && mine === seq.current && r.status === 'ok')
            setPreview(r.preview || '');
        })
        .catch(() => {});
    }, 400);
  };
  /* ขั้นยืนยันต้องโชว์ข้อความล่าสุดเสมอ (รีเช็ค 26 ต.ค.: เดิมใช้พรีวิวที่หน่วง 400 มิลลิวินาที กดเร็วได้ข้อความเก่า) */
  const toConfirm = () => {
    clearTimeout(timer.current);
    seq.current += 1;
    const value = text;
    setChecking(true);
    FetchData('OPTIONS', `/${mod}/preview_template`, token, {
      code: 'manual',
      template: value,
      _t: Date.now(),
    })
      .then((r) => (r.status === 'ok' ? r.preview || value : value))
      .catch(() => value)
      .then((pv) => {
        if (!alive.current) return;
        setChecking(false);
        setPreview(pv);
        setConfirmed({ text: value, preview: pv });
        setStep('confirm');
      });
  };
  const change = (value) => {
    setText(value);
    askPreview(value);
  };
  const pickPreset = (id) => {
    setPresetId(id);
    const p = presets.find((x) => Number(x.id) === Number(id));
    if (p) {
      change(p.message);
      setSaveName(p.name);
    } else {
      setSaveName(''); // พิมพ์ใหม่ = ชื่อชุดใหม่ (เดิมพกชื่อชุดก่อนมาบันทึกซ้ำชื่อ · รีเช็ค 27 ต.ค. 2569)
    }
  };
  const togglePick = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const savePreset = () => {
    if (saving || !text.trim() || !saveName.trim()) return;
    setSaving(true);
    FetchData('OPTIONS', `/${mod}/save_message`, token, {
      id: presetId || 0,
      name: saveName.trim(),
      message: text,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setSaving(false);
        if (r.status === 'ok') {
          onNotify(presetId ? 'แก้ชุดข้อความแล้ว' : 'บันทึกชุดข้อความแล้ว', 'success');
          setPresetId(Number(r.id) || 0);
          loadPresets();
        } else onNotify(ERR[r.msg] || 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
      })
      .catch(() => {
        if (alive.current) {
          setSaving(false);
          onNotify('บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };
  const deletePreset = () => {
    if (!presetId || saving) return;
    setAskDelete(false);
    setSaving(true);
    FetchData('OPTIONS', `/${mod}/delete_message`, token, { id: presetId, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        setSaving(false);
        if (r.status === 'ok') {
          onNotify('ลบชุดข้อความแล้ว', 'success');
          setPresetId(0);
          setSaveName('');
          loadPresets();
        } else onNotify(ERR[r.msg] || 'ลบไม่สำเร็จ', 'error');
      })
      .catch(() => {
        if (alive.current) {
          setSaving(false);
          onNotify('ลบไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };

  const send = () => {
    if (busy || !confirmed) return;
    setBusy(true);
    FetchData('OPTIONS', `/${mod}/send_message`, token, {
      channel_ids: picked,
      message: confirmed.text,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        if (r.status === 'ok') {
          const failed = (r.results || []).filter((x) => x.ok !== 'Y');
          if (failed.length)
            onNotify(
              `ส่งแล้ว ${r.sent} กลุ่ม · ไม่สำเร็จ ${failed.length} (${failed
                .map((x) => x.name)
                .join(', ')}) ดูสาเหตุในประวัติการส่ง`,
              'warning'
            );
          else onNotify(`ส่งข้อความเข้า ${r.sent} กลุ่มแล้ว`, 'success');
          onSent();
          onClose();
        } else onNotify(ERR[r.msg] || 'ส่งไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
      })
      .catch(() => {
        if (alive.current) {
          setBusy(false);
          /* เซิร์ฟเวอร์อาจส่งไปบางกลุ่มแล้ว — ไม่ชวนกดซ้ำ (กันข้อความเข้ากลุ่มซ้ำ) · โหลดประวัติให้ดู */
          onNotify('ไม่ทราบผลการส่ง — ดูในกลุ่มหรือประวัติการส่งก่อนกดส่งอีกครั้ง', 'warning');
          onSent();
        }
      });
  };

  const presetName = (presets.find((x) => Number(x.id) === Number(presetId)) || {}).name || '';
  const pickedNames = channels.filter((c) => picked.includes(Number(c.id))).map((c) => c.name);
  const canSend = picked.length > 0 && text.trim().length > 0 && text.length <= 2000;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>
        {step === 'edit' ? 'ส่งข้อความเข้ากลุ่ม Telegram' : 'ยืนยันก่อนส่ง'}
      </DialogTitle>
      <DialogContent dividers>
        {step === 'edit' ? (
          <MDBox display='flex' gap={2} flexWrap='wrap'>
            <MDBox flex='1 1 360px' minWidth={0}>
              <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
                ส่งเข้ากลุ่ม
              </MDTypography>
              <MDBox display='flex' flexWrap='wrap' gap={0.5} mb={1.5}>
                {channels.map((c) => (
                  <FormControlLabel
                    key={c.id}
                    sx={{ m: 0, mr: 1 }}
                    control={
                      <Checkbox
                        size='small'
                        checked={picked.includes(Number(c.id))}
                        onChange={() => togglePick(Number(c.id))}
                        inputProps={{ 'aria-label': `ส่งเข้ากลุ่ม ${c.name}` }}
                      />
                    }
                    label={<MDTypography variant='caption'>{c.name}</MDTypography>}
                  />
                ))}
                {channels.length === 0 && (
                  <MDTypography variant='caption' color='text'>
                    ยังไม่มีกลุ่มที่เปิดใช้งาน — เพิ่มกลุ่มในหน้านี้ก่อน
                  </MDTypography>
                )}
              </MDBox>
              <MDInput
                label='ชุดข้อความสำเร็จรูป'
                select
                fullWidth
                size='small'
                value={presetId}
                onChange={(e) => pickPreset(Number(e.target.value))}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              >
                <MenuItem value={0}>พิมพ์ใหม่</MenuItem>
                {presets.map((p) => (
                  <MenuItem key={p.id} value={Number(p.id)}>
                    {p.name}
                  </MenuItem>
                ))}
              </MDInput>
              <MDInput
                label='ข้อความ · ใช้ *ตัวหนา* ได้'
                fullWidth
                multiline
                minRows={7}
                value={text}
                onChange={(e) => change(e.target.value)}
                error={text.length > 2000}
                helperText={`${text.length}/2000 ตัวอักษร`}
                InputLabelProps={{ shrink: true }}
              />
              {/* บันทึกเป็นชุดข้อความ — ไว้กดส่งซ้ำรอบหน้า */}
              <MDBox display='flex' gap={1} alignItems='center' mt={1.5}>
                <MDInput
                  label='บันทึกเป็นชุดข้อความชื่อ'
                  size='small'
                  fullWidth
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <MDButton
                  size='small'
                  variant='outlined'
                  color='dark'
                  disabled={saving || !text.trim() || !saveName.trim()}
                  onClick={savePreset}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {presetId ? 'แก้ชุดนี้' : 'บันทึกชุด'}
                </MDButton>
                {presetId > 0 && (
                  <MDButton
                    size='small'
                    variant='text'
                    color='error'
                    disabled={saving}
                    onClick={() => setAskDelete(true)}
                    aria-label='ลบชุดข้อความนี้'
                    sx={{ minWidth: 0 }}
                  >
                    <Icon>delete_outline</Icon>
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
            <MDBox flex='1 1 300px' minWidth={0}>
              <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
                ตัวอย่างข้อความที่จะส่ง
              </MDTypography>
              <MDBox
                sx={{
                  bgcolor: '#E7F3FF',
                  borderRadius: 2,
                  p: 1.5,
                  minHeight: 180,
                  whiteSpace: 'pre-wrap',
                  fontSize: 14,
                  lineHeight: 1.5,
                  border: '1px solid #C9DFF5',
                }}
              >
                {preview || (text.trim() ? '…' : 'พิมพ์ข้อความด้านซ้าย')}
              </MDBox>
            </MDBox>
          </MDBox>
        ) : (
          <MDBox>
            <MDTypography variant='body2' fontWeight='medium' mb={1}>
              จะส่งเข้า {pickedNames.length} กลุ่ม: {pickedNames.join(' · ')}
            </MDTypography>
            <MDBox
              sx={{
                bgcolor: '#E7F3FF',
                borderRadius: 2,
                p: 1.5,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.5,
                border: '1px solid #C9DFF5',
              }}
            >
              {confirmed ? confirmed.preview : preview}
            </MDBox>
            <MDTypography variant='caption' color='text' display='block' mt={1}>
              ส่งแล้วเรียกคืนไม่ได้ · ประวัติอยู่ในตารางด้านล่างของหน้านี้
            </MDTypography>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'edit' ? (
          <>
            <MDButton variant='outlined' color='secondary' disabled={busy} onClick={onClose}>
              ยกเลิก
            </MDButton>
            <MDButton
              variant='gradient'
              color='dark'
              disabled={!canSend || checking}
              onClick={toConfirm}
            >
              {checking ? 'กำลังตรวจ…' : 'ตรวจก่อนส่ง'}
            </MDButton>
          </>
        ) : (
          <>
            <MDButton
              variant='outlined'
              color='secondary'
              disabled={busy}
              onClick={() => setStep('edit')}
              autoFocus
            >
              กลับไปแก้
            </MDButton>
            <MDButton variant='gradient' color='info' disabled={busy} onClick={send}>
              <Icon>send</Icon>&nbsp;{busy ? 'กำลังส่ง…' : `ส่งเข้า ${pickedNames.length} กลุ่ม`}
            </MDButton>
          </>
        )}
      </DialogActions>
      <Dialog open={askDelete} onClose={() => setAskDelete(false)} maxWidth='xs' fullWidth>
        <DialogTitle>ลบชุดข้อความ</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>ลบชุด “{presetName}” ? ลบแล้วเรียกคืนไม่ได้</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant='outlined'
            color='secondary'
            onClick={() => setAskDelete(false)}
            autoFocus
          >
            ยกเลิก
          </MDButton>
          <MDButton variant='gradient' color='error' onClick={deletePreset}>
            ลบ
          </MDButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

NotifySendDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  mod: PropTypes.string.isRequired,
  onSent: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
};
